import {useEffect, useRef, useState, useMemo, useContext} from "react";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import AuthContext from "context/AuthContext";
import NavBreadcrum from "components/NavBreadcrum";
import {ContainerSVG} from "components/waste-icons/svgs/ContainerSVG";
import axiApi from "ApiCaller";
import navs from "./navs";
import {MomentUtils} from "core/utils/MomentUtils";
import FormFleetRouteInfo from "./routes/components/FormFleetRouteInfo";
import FormFleetRouteIslands from "./routes/components/FormFleetRouteIslands";
import FormFleetRouteMap from "./routes/components/FormFleetRouteMap";
import FleetRouteGantt from "./routes/components/FleetRouteGantt";

const IconsForm = {
    s: <FontAwesomeIcon icon="location-dot" />,
    d: <FontAwesomeIcon icon="route" />,
    f: <FontAwesomeIcon icon="sack-xmark" />,
};

const EARTH_RADIUS_KM = 6371;

const buildPath = (telemetry) => {
    const pts = (telemetry || [])
        .filter((p) => p?.time != null && p?.lat != null && p?.lng != null)
        .map((p) => ({ms: p.time * 1000, lat: p.lat, lng: p.lng}))
        .sort((a, b) => a.ms - b.ms);
    if (pts.length < 2) return null;
    const toRad = (v) => (v * Math.PI) / 180;
    let acc = 0;
    const path = pts.map((p, i) => {
        if (i > 0) {
            const prev = pts[i - 1];
            const dLat = toRad(p.lat - prev.lat);
            const dLng = toRad(p.lng - prev.lng);
            const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(prev.lat)) * Math.cos(toRad(p.lat)) * Math.sin(dLng / 2) ** 2;
            acc += 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
        }
        return {...p, dist: acc};
    });
    return {path, totalDist: acc};
};

const positionAtPath = (pathInfo, d) => {
    if (!pathInfo || d == null) return null;
    const {path, totalDist} = pathInfo;
    if (d <= 0) return {lat: path[0].lat, lng: path[0].lng, ms: path[0].ms};
    const last = path[path.length - 1];
    if (d >= totalDist) return {lat: last.lat, lng: last.lng, ms: last.ms};
    let lo = 0;
    let hi = path.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (path[mid].dist < d) lo = mid + 1;
        else hi = mid;
    }
    const b = path[lo];
    const a = path[lo - 1];
    const seg = b.dist - a.dist;
    const ratio = seg > 0 ? (d - a.dist) / seg : 0;
    return {
        lat: a.lat + (b.lat - a.lat) * ratio,
        lng: a.lng + (b.lng - a.lng) * ratio,
        ms: a.ms + (b.ms - a.ms) * ratio,
    };
};

const FormFleetRoute = () => {
    const {t: _Tr} = useTranslation("routes");
    const {t: _Tc} = useTranslation("choices");
    const {idParams} = useParams();
    const {panel} = useContext(AuthContext);
    const {momentTz} = MomentUtils();

    const bread = useMemo(() => [
        {name: _Tr("labels.fleet-management"), url: "/fleet-management/", current: false},
        {name: _Tr("labels.fleet-routes"), url: "/fleet-management/routes/", current: false},
        {name: _Tr("labels.fleet-route"), current: true},
    ], [_Tr]);

    document.title = _Tr("labels.fleet-route");

    const [route, setRoute] = useState({});
    const [session, setSession] = useState(null);
    const [executed, setExecuted] = useState([]);
    const [telemetry, setTelemetry] = useState([]);
    const [executedTrucks, setExecutedTrucks] = useState([]);
    const [highlighted, setHighligthed] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [cursorMs, setCursorMs] = useState(null);
    const progressRef = useRef(null);
    const truckUpdaterRef = useRef(null);
    const ganttUpdaterRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    // tras un refresco, el camión va al último punto GPS cuando se recalcule el trazado
    const goLiveRef = useRef(false);

    const styleList = useRef({
        maxWidth: "500px",
        height: "calc(100vh - 340px)",
        overflow: "hidden",
        overflowY: "scroll",
    });

    // carga común: la usan la carga inicial y el botón de refrescar
    const fetchRoute = () =>
        axiApi.get(`/api/fleet/routes/${idParams}/`).then((resp) => {
            if (resp.data.status) {
                setRoute(resp.data.route || {});
                setSession(resp.data.session || null);
                setExecuted([...(resp.data.executed_waypoints || [])]);
                setTelemetry([...(resp.data.telemetry || [])]);
                setExecutedTrucks([...(resp.data.executed_trucks || [])]);
            }
        });

    useEffect(() => {
        setCursorMs(null);
        setPlaying(false);
        fetchRoute()
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [idParams]);

    const checkpoints = useMemo(() => route?.checkpoints || [], [route]);

    const islands = useMemo(() => {
        const execByOrder = {};
        executed.forEach((w) => {
            execByOrder[String(w.order)] = w;
        });
        return checkpoints.map((cp) => ({
            ...cp,
            executed: execByOrder[String(cp.order)] || null,
        }));
    }, [checkpoints, executed]);

    const durationReal = useMemo(() => {
        if (!session?.started_at) return null;
        const end = session.completed_at || session.returned_at || session.closed_at;
        if (!end) return null;
        const startMs = momentTz(session.started_at)?.valueOf();
        const endMs = momentTz(end)?.valueOf();
        if (startMs == null || endMs == null) return null;
        const diff = (endMs - startMs) / 1000;
        if (!diff || diff < 0) return null;
        return Math.round(diff);
    }, [session]);

    const playbackRange = useMemo(() => {
        const gpsMs = telemetry
            .map((p) => (p?.time != null ? p.time * 1000 : null))
            .filter((t) => t != null);
        const stopMs = executed
            .map((w) => (w?.arrived_at ? momentTz(w.arrived_at)?.valueOf() ?? null : null))
            .filter((t) => t != null)
            .sort((a, b) => a - b);
        const sessionStartMs = session?.started_at ? momentTz(session.started_at)?.valueOf() ?? null : null;
        const sessionEndMs = [
            session?.returned_at,
            session?.completed_at,
            session?.closed_at,
        ]
            .map((t) => (t ? momentTz(t)?.valueOf() ?? null : null))
            .filter((t) => t != null)
            .sort((a, b) => a - b);
        const startCandidates = [gpsMs[0], sessionStartMs, stopMs[0]].filter((t) => t != null);
        const endCandidates = [
            sessionEndMs[sessionEndMs.length - 1],
            gpsMs[gpsMs.length - 1],
            stopMs[stopMs.length - 1],
        ].filter((t) => t != null);
        if (!startCandidates.length || !endCandidates.length) return null;
        const startMs = Math.min(...startCandidates);
        const endMs = Math.max(...endCandidates);
        if (endMs === startMs) return {start: startMs, end: startMs + 60000};
        return {start: startMs, end: endMs};
    }, [telemetry, executed, session]);

    const pathInfo = useMemo(() => buildPath(telemetry), [telemetry]);

    const updateCursor = (d, commit) => {
        const pos = positionAtPath(pathInfo, d);
        if (!pos) return;
        truckUpdaterRef.current?.(pos.lat, pos.lng);
        ganttUpdaterRef.current?.(pos.ms);
        if (commit) setCursorMs(pos.ms);
    };

    const handlePlayPause = () => {
        if (!pathInfo || pathInfo.totalDist <= 0) return;
        if (playing) {
            updateCursor(progressRef.current, true);
            setPlaying(false);
            return;
        }
        if (progressRef.current == null || progressRef.current >= pathInfo.totalDist) {
            progressRef.current = 0;
        }
        updateCursor(progressRef.current, true);
        setPlaying(true);
    };

    const handleReset = () => {
        setPlaying(false);
        progressRef.current = 0;
        updateCursor(0, true);
    };

    // trae los datos nuevos sin spinner de página; el camión queda en la posición actual
    const handleRefresh = () => {
        setPlaying(false);
        if (pathInfo) {
            progressRef.current = pathInfo.totalDist;
            updateCursor(pathInfo.totalDist, true);
        }
        setRefreshing(true);
        goLiveRef.current = true;
        fetchRoute()
            .catch((err) => {
                goLiveRef.current = false;
                console.error(err);
            })
            .finally(() => {
                setRefreshing(false);
            });
    };

    useEffect(() => {
        if (!goLiveRef.current) return;
        goLiveRef.current = false;
        if (!pathInfo) return;
        progressRef.current = pathInfo.totalDist;
        updateCursor(pathInfo.totalDist, true);
    }, [pathInfo]);


    useEffect(() => {
        if (!playing || !pathInfo) return;
        const {totalDist} = pathInfo;
        if (progressRef.current == null) progressRef.current = 0;
        const speed = totalDist / 30000;
        let lastTs = null;
        const timer = setInterval(() => {
            const now = Date.now();
            if (lastTs == null) lastTs = now;
            const delta = now - lastTs;
            lastTs = now;
            progressRef.current = Math.min(progressRef.current + delta * speed, totalDist);
            updateCursor(progressRef.current, false);
            if (progressRef.current >= totalDist) {
                updateCursor(progressRef.current, true);
                setPlaying(false);
            }
        }, 50);
        return () => clearInterval(timer);
    }, [playing, pathInfo]);



    if (loading) {
        return (
            <>
                <NavBreadcrum navs={navs} bread={bread} />
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <div className="col-12 preview-canvas offcanvas-header mt-1">
                <div className="preview-icon">{IconsForm[route.type] || IconsForm.s}</div>
                <div className="preview-title ps-4">
                    <h5>
                        {_Tr("labels.route")}: <strong>{route.name}</strong>
                    </h5>
                    <h6>
                        Prog: <strong>{route.prog_name}</strong>
                    </h6>
                </div>
                <div className="ms-auto text-secondary pe-3">
                    <div className="text-center">{_Tc("wastes." + route.waste_type)}</div>
                    <div className="text-center">
                        <ContainerSVG type={route.waste_type} />
                    </div>
                    <div className="text-center">{_Tc("downloads." + route.download_type)}</div>
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <FormFleetRouteInfo
                        route={route}
                        session={session}
                        executed={executed}
                        executedTrucks={executedTrucks}
                        durationReal={durationReal}
                    />
                </div>
                <div className="col" style={styleList.current}>
                    <FormFleetRouteIslands
                        islands={islands}
                        wasteType={route.waste_type}
                        highlighted={highlighted}
                        setHighligthed={setHighligthed}
                    />
                </div>
                <div className="col d-flex flex-column" style={{height: "calc(100vh - 340px)"}}>
                    <div style={{flex: "1 1 auto", minHeight: "0", width: "100%"}}>
                        <FormFleetRouteMap
                            center={panel.coords}
                            checkpoints={checkpoints}
                            geojson={route.geojson || {}}
                            telemetry={telemetry}
                            highlightedPoint={highlighted}
                            styleMap={{height: "100%", width: "100%"}}
                            wasteType={route.waste_type}
                            truckUpdaterRef={truckUpdaterRef}
                        />
                    </div>
                    <FleetRouteGantt
                        route={route}
                        session={session}
                        executed={executed}
                        telemetry={telemetry}
                        cursorMs={cursorMs}
                        playing={playing}
                        playbackRange={playbackRange}
                        pathInfo={pathInfo}
                        onPlayPause={handlePlayPause}
                        onReset={handleReset}
                        refreshing={refreshing}
                        onRefresh={handleRefresh}
                        ganttUpdaterRef={ganttUpdaterRef}
                    />
                </div>
            </div>
        </>
    );
};
export default FormFleetRoute;
